import { LineChart } from '@mantine/charts';
import { Card, Group, Stack, Text, Title } from '@mantine/core';
import { useDeferredValue, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Activity } from 'lucide-react';

import { useBaselineCalculation } from '../../../../hooks/useBaselineCalculation';
import { useHeartRateStore } from '@store/useHeartRateStore';
import { useSession } from '../../../../hooks/useSession';
import { ReferenceLine } from '@types';
import { getAppConfig } from '@configs/apps/appConfigs';
import { getFoodNamesByIds } from '../../../../data/foods';
import { calculateHeartRateVariation, StatusKey } from '@utils';
import { generateReferenceLines } from '@utils/chartUtils';
import {
  formatElapsedTimeFromStart,
  formatTimestampToTime,
} from '@utils/formatDuration';

export type FeedbackAtPoint = {
  itemIds: number[];
  score: number;
  timestamp: number;
};

interface HeartRateChartsProps {
  title?: string;
}

export const HeartRateCharts = ({ title }: HeartRateChartsProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { session } = useSession(id);

  const events = useHeartRateStore(s => s.events);
  const exposureEvents = useHeartRateStore(s => s.exposureEvents);
  const deferredEvents = useDeferredValue(events);
  const { baseline } = useBaselineCalculation();
  const variation = session?.config?.variation;
  const currentState = session?.config?.currentState;
  const hasVariation = getAppConfig(session?.type ?? 'default').hasVariation;

  // Reference lines: per sessioni monitoraggio (exposure, wellbeing, ecc.) solo Baseline; per performance tutti i livelli
  const allReferenceLines: ReferenceLine[] = useMemo(() => {
    if (baseline <= 0) return [];

    if (!hasVariation) {
      return [
        {
          y: baseline,
          color: 'blue',
          label: `Baseline: ${Math.round(baseline)} bpm`,
          strokeWidth: 3,
        },
      ];
    }

    if (!variation) return [];

    try {
      const variationLevels = calculateHeartRateVariation(
        baseline,
        variation,
        (currentState as StatusKey) ?? 'p0'
      );
      return generateReferenceLines(variationLevels);
    } catch (error) {
      console.error('Error creating reference lines:', error);
      return [];
    }
  }, [baseline, variation, currentState, hasVariation]);

  // Get session start timestamp from localStorage (used by chart data and feedback lines)
  const sessionStartTimestamp = useMemo(() => {
    if (!id) return null;
    const TIMER_START_KEY = `session_start_${id}`;
    const saved = localStorage.getItem(TIMER_START_KEY);
    return saved ? parseInt(saved, 10) : null;
  }, [id]);

  // Transform events: timeLabel + per sessioni exposure segno il primo evento HR successivo a ogni feedback
  const chartData = useMemo(() => {
    const base = deferredEvents.map(event => ({
      ...event,
      timeLabel: formatElapsedTimeFromStart(
        event.timestamp,
        sessionStartTimestamp
      ),
      feedback: [] as FeedbackAtPoint[],
    }));

    if (session?.type !== 'exposure' || base.length === 0) return base;

    const feedbacks = exposureEvents.filter(
      (
        e
      ): e is {
        type: 'itemFeedback';
        timestamp: number;
        itemIds: number[];
        score: number;
      } => e.type === 'itemFeedback'
    );

    for (const fb of feedbacks) {
      const firstAfterIndex = base.findIndex(p => p.timestamp >= fb.timestamp);
      if (firstAfterIndex === -1) continue;
      const itemIds = Array.isArray(fb.itemIds)
        ? fb.itemIds
        : typeof (fb as unknown as { foodId?: number }).foodId === 'number'
          ? [(fb as unknown as { foodId: number }).foodId]
          : [];
      base[firstAfterIndex].feedback.push({
        itemIds,
        score: fb.score,
        timestamp: fb.timestamp,
      });
    }

    return base;
  }, [deferredEvents, sessionStartTimestamp, session?.type, exposureEvents]);

  const timeDomainReferenceLines = allReferenceLines;

  // Mostra il grafico LF/HF solo se almeno un dato LF o HF è presente (> 0)
  const showFrequencyDomain = useMemo(() => {
    // Filtra solo gli eventi con dati di frequenza validi
    const eventsWithFreqData = deferredEvents.filter(
      e => (e?.lf ?? 0) > 0 || (e?.hf ?? 0) > 0
    );
    return eventsWithFreqData.length > 0;
  }, [deferredEvents]);

  // Usa solo eventi con dati di frequenza per il grafico LF/HF
  const eventsForFreqChart = useMemo(() => {
    return deferredEvents.filter(e => (e?.lf ?? 0) > 0 || (e?.hf ?? 0) > 0);
  }, [deferredEvents]);

  // Transform events for frequency chart with formatted elapsed time label
  const eventsForFreqChartFormatted = useMemo(() => {
    return eventsForFreqChart.map(event => ({
      ...event,
      timeLabel: formatElapsedTimeFromStart(
        event.timestamp,
        sessionStartTimestamp
      ),
    }));
  }, [eventsForFreqChart, sessionStartTimestamp]);

  // ─── Empty state ────────────────────────────────────────────────
  if (deferredEvents.length === 0) {
    return (
      <Stack gap='md'>
        {title && (
          <Group justify='space-between' align='center'>
            <Title order={4}>{title}</Title>
          </Group>
        )}

        <Card>
          <Card.Section inheritPadding py='xl'>
            <Stack gap={10} align='center'>
              <Activity
                size={64}
                strokeWidth={1.5}
                color='var(--mantine-color-dimmed)'
              />
              <Title order={5} ta='center' fw={500} c='dimmed'>
                {t('no_data_available', 'Nessun dato disponibile')}
              </Title>
              <Text size='sm' c='dimmed'>
                {t(
                  'start_recording_to_see_data',
                  'Inizia la registrazione per vedere i dati del grafico'
                )}
              </Text>
            </Stack>
          </Card.Section>
        </Card>
      </Stack>
    );
  }

  // ─── Chart state ────────────────────────────────────────────────
  return (
    <Stack gap='md'>
      {/* Domino Temporale (HR & HRV) */}
      <Card withBorder shadow='sm'>
        <Card.Section withBorder inheritPadding py='xs'>
          <Stack gap={4}>
            <Group justify='space-between' align='center'>
              <Title order={4}>
                {t('time_domain_chart', 'Dominio Temporale (HR & HRV)')}
              </Title>
            </Group>
            {!hasVariation && (
              <Text size='xs' c='dimmed'>
                Monitoraggio opzionale HR e/o HRV
              </Text>
            )}
          </Stack>
        </Card.Section>
        <Card.Section inheritPadding py='md'>
          <LineChart
            w='100%'
            h={400}
            withDots={false}
            data={chartData}
            dataKey='timeLabel'
            type='gradient'
            strokeWidth={5}
            curveType='bump'
            tooltipAnimationDuration={200}
            gradientStops={[
              { offset: 0, color: 'red' },
              { offset: 57, color: 'orange' },
              { offset: 85, color: 'green' },
              { offset: 100, color: 'blue' },
            ]}
            series={[
              { name: 'hr', color: 'indigo.6', label: 'HR (BPM)' },
              { name: 'hrv', color: 'orange.6', label: 'HRV (ms)' },
            ]}
            referenceLines={timeDomainReferenceLines}
            lineProps={series =>
              series.name === 'hr'
                ? {
                    dot: (props: {
                      cx?: number;
                      cy?: number;
                      payload?: { feedback?: FeedbackAtPoint[] };
                    }) =>
                      props.payload?.feedback?.length ? (
                        <circle
                          cx={props.cx}
                          cy={props.cy}
                          r={6}
                          fill='var(--mantine-color-violet-6)'
                          stroke='white'
                          strokeWidth={2}
                        />
                      ) : (
                        <g />
                      ),
                  }
                : {}
            }
            tooltipProps={{
              content: ({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0]?.payload as
                  | { feedback?: FeedbackAtPoint[]; hr?: number; hrv?: number }
                  | undefined;
                const feedbacks = p?.feedback ?? [];
                return (
                  <Stack
                    gap={4}
                    p='xs'
                    style={{
                      background: 'var(--mantine-color-default)',
                      color: 'var(--mantine-color-text)',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  >
                    <Text size='xs' fw={500}>
                      {label}
                    </Text>
                    {payload.map(item => (
                      <Text key={item.name} size='xs' component='span'>
                        {item.name}: {item.value}
                      </Text>
                    ))}
                    {feedbacks.length > 0 && (
                      <>
                        <Text size='xs' fw={600} mt={4} mb={2}>
                          Feedback cibo
                        </Text>
                        {feedbacks.map((fb, i) => (
                          <Text key={i} size='xs' component='span'>
                            {(fb.itemIds?.length ?? 0) > 0
                              ? getFoodNamesByIds(fb.itemIds ?? [])
                              : '--'}{' '}
                            — score: {fb.score},{' '}
                            {formatTimestampToTime(fb.timestamp)}
                          </Text>
                        ))}
                      </>
                    )}
                  </Stack>
                );
              },
            }}
          />
        </Card.Section>
      </Card>

      {/* Dominio Frequenziale (LF & HF) */}
      {showFrequencyDomain && (
        <Card>
          <Card.Section withBorder inheritPadding py='xs'>
            <Group justify='space-between' align='center'>
              <Title order={4}>
                {t('frequency_domain_chart', 'Dominio Frequenziale (LF & HF)')}
              </Title>
            </Group>
          </Card.Section>
          <Card.Section py='xs'>
            <LineChart
              h={400}
              w='100%'
              withDots={false}
              data={eventsForFreqChartFormatted}
              dataKey='timeLabel'
              type='gradient'
              strokeWidth={5}
              curveType='bump'
              tooltipAnimationDuration={200}
              series={[
                { name: 'lf', color: 'purple.6', label: 'LF (ms²)' },
                { name: 'hf', color: 'cyan.6', label: 'HF (ms²)' },
              ]}
            />
          </Card.Section>
        </Card>
      )}
    </Stack>
  );
};
